import { ImageFileValidationErrors } from '@pixum/types/lib/image-file-validation-service/image-file-validation-errors';
import type { ImageUploadService } from '@pixum/types/lib/image-upload-service/image-upload-service';
import {
  DialogIdentifier,
  UploadErrorType,
  type BaseUploadError,
  type DialogService,
  type MapUploadErrorToUploadAction,
  type StateService,
  type UploadErrorStateActions
} from '@pixum/types';
import type { ImageStateUpdater } from '../../../../types/expressDesigner';
import type { MapUploadErrorToUploadActionProps } from './types';

interface OpenDialogForUploadErrorProps {
  dialogMode: DialogIdentifier;
  dialogService: DialogService;
  imageStateActions: ImageStateUpdater;
  imagesStateService: StateService<unknown>;
  uploadErrorStateActions: UploadErrorStateActions;
  uploadService: ImageUploadService;
  constants: Record<string, string>;
}

const openDialogForUploadError =
  ({
    dialogService,
    dialogMode,
    imageStateActions,
    imagesStateService,
    uploadErrorStateActions,
    uploadService,
    constants
  }: OpenDialogForUploadErrorProps) =>
  async (uploadErrors: BaseUploadError[], numOfFilesToBeUploaded: number) => {
    await dialogService
      .openDialog(dialogMode, {
        numOfFilesToBeUploaded,
        imageStateActions,
        imagesStateService,
        uploadErrors,
        uploadErrorStateActions,
        uploadService,
        constants
      })
      .catch(() => imageStateActions.removeImages(uploadErrors.map(({ uuid }) => uuid)));
  };

export const createMapUploadErrorToUploadAction = ({
  dialogService,
  imageStateActions,
  imagesStateService,
  uploadErrorStateActions,
  uploadService,
  constants
}: MapUploadErrorToUploadActionProps): MapUploadErrorToUploadAction => ({
  [ImageFileValidationErrors.WRONG_FORMAT]: (uploadErrors, numOfFilesToBeUploaded) =>
    openDialogForUploadError({
      dialogMode: DialogIdentifier.UPLOAD_ERROR_IMAGE_FILETYPE,
      dialogService,
      imageStateActions,
      imagesStateService,
      uploadErrorStateActions,
      uploadService,
      constants
    })(uploadErrors, numOfFilesToBeUploaded),

  [ImageFileValidationErrors.TOO_SMALL]: (uploadErrors, numOfFilesToBeUploaded) =>
    openDialogForUploadError({
      dialogMode: DialogIdentifier.UPLOAD_ERROR_IMAGE_FILE_TOO_SMALL,
      dialogService,
      imageStateActions,
      imagesStateService,
      uploadErrorStateActions,
      uploadService,
      constants
    })(uploadErrors, numOfFilesToBeUploaded),

  [ImageFileValidationErrors.TOO_BIG]: (uploadErrors, numOfFilesToBeUploaded) =>
    openDialogForUploadError({
      dialogMode: DialogIdentifier.UPLOAD_ERROR_IMAGE_FILE_TOO_BIG,
      dialogService,
      imageStateActions,
      imagesStateService,
      uploadErrorStateActions,
      uploadService,
      constants
    })(uploadErrors, numOfFilesToBeUploaded),

  [ImageFileValidationErrors.FILE_RESOLUTION_TOO_HIGH]: (uploadErrors, numOfFilesToBeUploaded) =>
    openDialogForUploadError({
      dialogMode: DialogIdentifier.UPLOAD_ERROR_IMAGE_TOO_HIGH_RESOLUTION,
      dialogService,
      imageStateActions,
      imagesStateService,
      uploadErrorStateActions,
      uploadService,
      constants
    })(uploadErrors, numOfFilesToBeUploaded),

  [UploadErrorType.UPLOAD_FAILED]: (uploadErrors, numOfFilesToBeUploaded) =>
    openDialogForUploadError({
      dialogMode: DialogIdentifier.UPLOAD_ERROR_RETRY,
      dialogService,
      imageStateActions,
      imagesStateService,
      uploadErrorStateActions,
      uploadService,
      constants
    })(uploadErrors, numOfFilesToBeUploaded),

  [UploadErrorType.UPLOAD_MAX_REACHED]: (uploadErrors, numOfFilesToBeUploaded) =>
    openDialogForUploadError({
      dialogMode: DialogIdentifier.UPLOAD_ERROR_UPLOAD_FAILED_COMPLETELY,
      dialogService,
      imageStateActions,
      imagesStateService,
      uploadErrorStateActions,
      uploadService,
      constants
    })(uploadErrors, numOfFilesToBeUploaded),

  [ImageFileValidationErrors.SERVER_VALIDATION_FAILED]: (uploadErrors, numOfFilesToBeUploaded) =>
    openDialogForUploadError({
      dialogMode: DialogIdentifier.UPLOAD_ERROR_UPLOAD_FAILED_COMPLETELY,
      dialogService,
      imageStateActions,
      imagesStateService,
      uploadErrorStateActions,
      uploadService,
      constants
    })(uploadErrors, numOfFilesToBeUploaded)
});
